import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { LayoutModule } from './views/layout/layout.module';
import { AuthGuard } from './core/guard/auth.guard';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { AngularFireModule } from '@angular/fire';
import { environment } from '../environments/environment';
import { AngularFirestoreModule } from '@angular/fire/firestore';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorPageComponent } from './views/pages/error-page/error-page.component';
import { HIGHLIGHT_OPTIONS } from 'ngx-highlightjs';
import { AuthService } from './core/auth/auth.service';
import { UserService } from './users/services/user.service';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader} from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import {ToastrModule} from 'ngx-toastr';
import { ConfirmMailComponent } from './core/auth/confirm-mail/confirm-mail.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { LoggedInAuthGuardGuard } from './core/guard/logged-in-auth-guard.guard';
import { AdminVerificationComponent } from './core/auth/admin-verification/admin-verification.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { UserBlockedComponent } from './core/auth/user-blocked/user-blocked.component';
import { AngularFireAnalyticsModule } from '@angular/fire/analytics';
import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';
import { Integrations } from '@sentry/tracing';
import { SubmitLectureStudentsComponent } from './submit-lecture/submit-lecture-students.component';
import { SubmitLectureAdultsComponent } from './submit-lecture/submit-lecture-adults.component';
import { MigrateLecturesComponent } from './migration/migrate-lectures/migrate-lectures.component';
import { ModifyLecturesComponent } from './migration/modify-lectures/modify-lectures.component';
import { CityDetailsComponent } from './views/pages/lectures/cities/city-details/city-details.component';
import { UserScoreService } from './users/services/user-score.service';
import { MigrateUsersProgressComponent } from './migration/migrate-users-progress/migrate-users-progress.component';

// import { RecaptchaModule, RECAPTCHA_SETTINGS, RecaptchaSettings, RecaptchaFormsModule } from 'ng-recaptcha';

// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory(): any {
  return player;
}

export function HttpLoaderFactory(httpClient: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient);
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorPageComponent,
    ConfirmMailComponent,
    SubmitLectureStudentsComponent,
    SubmitLectureAdultsComponent,
    AdminVerificationComponent,
    UserBlockedComponent,
    MigrateLecturesComponent,
    ModifyLecturesComponent,
    CityDetailsComponent,
    MigrateUsersProgressComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    LayoutModule,
    BrowserModule,
    ReactiveFormsModule,
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAnalyticsModule,
    AngularFirestoreModule,
    AngularFireAuthModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    SharedModule.forRoot(),
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-center',
      preventDuplicates: true,
      progressBar: true
    }),
    ReactiveFormsModule,
    NgSelectModule,
    FormsModule,
    MatAutocompleteModule,
    LottieModule.forRoot({ player: playerFactory })
  ],
  providers: [
    AuthService,
    AuthGuard,
    LoggedInAuthGuardGuard,
    UserService,
    UserScoreService,
    {
      provide: HIGHLIGHT_OPTIONS, // https://www.npmjs.com/package/ngx-highlightjs
      useValue: {
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
          typescript: () => import('highlight.js/lib/languages/typescript'),
          scss: () => import('highlight.js/lib/languages/scss'),
        }
      }
    },
    // {
    //   provide: RECAPTCHA_SETTINGS,
    //   useValue: { siteKey: '6LeukUwaAAAAABmMKqxdysXOn7a3LVIgGCIHYSSR' } as RecaptchaSettings,
    // }
  ],
  exports: [TranslateModule],
  bootstrap: [AppComponent]
})
export class AppModule {
  // constructor(private readonly firestore: AngularFirestore) {
  //   firebase.firestore().enablePersistence()
  //       .then(() => console.log('enable cache mode successfully'))
  //       .catch(err => console.log(`enable cache mode error: ${err}`));
  // }
}

Sentry.init({
  dsn: 'https://096db833df544545b4c29bafe192f790@o696789.ingest.sentry.io/5776436',
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    new Integrations.BrowserTracing({
      tracingOrigins: ['https://spaceil-dev*', 'https://spaceil-portal-*'],
      routingInstrumentation: Sentry.routingInstrumentation,
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});
enableProdMode();
// platformBrowserDynamic()
//     .bootstrapModule(AppModule)
//     .then(success => console.log(`Bootstrap success`))
//     .catch(err => console.error(err));
